import { useId, useState, type ReactNode } from 'react'

interface NumberInputProps {
  label: string
  value: number | null
  onChange: (value: number | null) => void
  unit?: string
  hint?: ReactNode
  error?: string
  min?: number
  max?: number
  placeholder?: string
}

/**
 * A number field.
 *
 * Two decisions worth keeping:
 *
 * 1. **`inputMode="decimal"` on a text input, not `type="number"`.** A number
 *    input silently drops what it cannot parse, scrolls its value on a
 *    trackpad, and renders spinners nobody in this design system wants.
 * 2. **A comma is a decimal separator.** An Indonesian keyboard produces `,`,
 *    and this app is built for that market first — rejecting `1,5` would be a
 *    defect in the primary locale, not an edge case.
 *
 * `tabular-nums` because the calorie and portion figures change while the user
 * watches them, and a proportional font makes the field jitter per keystroke
 * (design system § Type).
 *
 * **The draft state is what makes the comma work.** A controlled field that
 * re-renders from the parsed number destroys the separator the instant it is
 * typed: `"72,"` parses to `72`, the field re-renders as `"72"`, and the next
 * keystroke appends — so `72,4` becomes **724**. A review caught this producing
 * a 724 kg weight, which is worse than rejecting the character would have been.
 * While the field has focus the user's own text stands; on blur it is parsed and
 * committed. `Stepper` already worked this way, so the fix was in the diff.
 *
 * SUAP-0103.
 */
export function parseDecimal(raw: string): number | null {
  const normalised = raw.replace(',', '.').trim()
  if (normalised === '') return null
  const value = Number(normalised)
  return Number.isFinite(value) ? value : null
}

export function NumberInput({
  label,
  value,
  onChange,
  unit,
  hint,
  error,
  min,
  max,
  placeholder,
}: NumberInputProps) {
  const id = useId()
  const [draft, setDraft] = useState<string | null>(null)
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>

      <div
        className={[
          'flex min-h-11 items-center gap-2 rounded-input border bg-surface px-3',
          'focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink',
          error === undefined ? 'border-hairline' : 'border-over',
        ].join(' ')}
      >
        <input
          id={id}
          type="text"
          inputMode="decimal"
          value={draft ?? (value === null ? '' : String(value))}
          placeholder={placeholder}
          aria-invalid={error === undefined ? undefined : true}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          {...(min === undefined ? {} : { 'aria-valuemin': min })}
          {...(max === undefined ? {} : { 'aria-valuemax': max })}
          onChange={(event) => {
            const raw = event.target.value
            setDraft(raw)
            // The parent still sees every keystroke's parsed value, so a form can
            // validate live — it just does not get to rewrite what is on screen.
            onChange(parseDecimal(raw))
          }}
          onBlur={() => {
            if (draft !== null) onChange(parseDecimal(draft))
            setDraft(null)
          }}
          // self-stretch, not padding: the input is the touch target and must be
          // the full 44px of its wrapper (SUAP-0103 verification).
          className="min-h-11 min-w-0 flex-1 self-stretch bg-transparent text-base tabular-nums text-ink outline-none placeholder:text-ink-soft"
        />
        {unit === undefined ? null : (
          <span aria-hidden className="shrink-0 text-sm text-ink-soft">
            {unit}
          </span>
        )}
      </div>

      {hint === undefined ? null : (
        <p id={hintId} className="text-xs text-ink-soft">
          {hint}
        </p>
      )}
      {error === undefined ? null : (
        <p id={errorId} className="text-xs text-over">
          {error}
        </p>
      )}
    </div>
  )
}
